// PKCE utilities
const generateCodeVerifier = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode.apply(null, array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

const generateCodeChallenge = async (verifier) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode.apply(null, new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

// StreetLib OAuth Configuration
export const OAUTH_CONFIG = {
  // Environment-specific URLs
  AUTH_URL: process.env.AUTH_URL || 'https://staging.auth.streetlib.com',
  
  // Client configuration
  CLIENT_ID: process.env.USER_POOL_CLIENT_ID || '3a29lqifkbegg0h37ls5q21so5',
  
  // Scopes
  SCOPES: 'openid profile email',
  
  // Redirect URI (will be set dynamically based on current domain)
  getRedirectUri: () => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}/auth/oauth/streetlib/callback`;
  },
  
  // App name for OAuth flow
  APP_NAME: 'StreetLib Dashboard',
  
  // Token endpoints
  ENDPOINTS: {
    AUTHORIZE: '/api/oauth/authorize',
    TOKEN: '/api/oauth/token',
    USERINFO: '/api/oauth/userinfo',
    REFRESH: '/api/oauth/token'
  }
};

// Generate state parameter for CSRF protection
export const generateState = () => {
  if (typeof window === 'undefined') return '';
  return Array.from(crypto.getRandomValues(new Uint8Array(32)))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
};

// Build authorization URL with PKCE
export const buildAuthUrl = async (state) => {
  const { AUTH_URL, CLIENT_ID, SCOPES, APP_NAME, ENDPOINTS } = OAUTH_CONFIG;
  const redirectUri = OAUTH_CONFIG.getRedirectUri();
  
  // Log the configuration being used
  // eslint-disable-next-line no-console
  console.log('🔧 OAuth Configuration:', {
    AUTH_URL,
    CLIENT_ID,
    redirectUri,
    endpoint: ENDPOINTS.AUTHORIZE,
    environment: process.env.NODE_ENV
  });
  
  // Generate PKCE parameters
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  
  // Store code_verifier for later use
  if (typeof window !== 'undefined') {
    window.sessionStorage.setItem('oauth_code_verifier', codeVerifier);
  }
  
  const url = new URL(`${AUTH_URL}${ENDPOINTS.AUTHORIZE}`);
  url.searchParams.set('client_id', CLIENT_ID);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', SCOPES);
  url.searchParams.set('redirect_uri', redirectUri);
  url.searchParams.set('state', state);
  url.searchParams.set('code_challenge', codeChallenge);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('app_name', APP_NAME);
  
  const finalUrl = url.toString();
  // eslint-disable-next-line no-console
  console.log('🚀 Built OAuth URL:', finalUrl);
  
  return finalUrl;
};

// Exchange authorization code for tokens
export const exchangeCodeForTokens = async (code) => {
  const { AUTH_URL, CLIENT_ID, ENDPOINTS } = OAUTH_CONFIG;
  const redirectUri = OAUTH_CONFIG.getRedirectUri();
  
  // Get stored code_verifier
  const codeVerifier = typeof window !== 'undefined' 
    ? window.sessionStorage.getItem('oauth_code_verifier')
    : null;
  
  if (!codeVerifier) {
    throw new Error('Code verifier not found. Please restart the OAuth flow.');
  }
  
  // Validate code format
  if (!code || code.length < 10) {
    throw new Error('Invalid authorization code format.');
  }
  
  try {
    const requestBody = {
      grant_type: 'authorization_code',
      client_id: CLIENT_ID,
      code,
      redirect_uri: redirectUri,
      code_verifier: codeVerifier
    };
    
    // eslint-disable-next-line no-console
    console.log('🔄 Exchanging authorization code for tokens...', {
      client_id: CLIENT_ID,
      redirect_uri: redirectUri,
      code_length: code.length,
      code_verifier_present: !!codeVerifier
    });
    
    const response = await fetch(`${AUTH_URL}${ENDPOINTS.TOKEN}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });
    
    if (!response.ok) {
      let errorDetails;
      try {
        errorDetails = await response.json();
      } catch {
        errorDetails = await response.text();
      }
      
      // Handle specific error cases
      if (response.status === 400 && errorDetails.error === 'invalid_grant') {
        throw new Error('Authorization code is invalid or expired. Please try logging in again.');
      } else if (response.status === 500) {
        // Log detailed error for debugging
        // eslint-disable-next-line no-console
        console.error('🚨 OAuth Server Error Details:', {
          status: response.status,
          error: errorDetails,
          requestPayload: {
            grant_type: 'authorization_code',
            client_id: CLIENT_ID,
            code: `${code.substring(0, 8)}...`,
            redirect_uri: redirectUri,
            code_verifier_length: codeVerifier.length
          },
          timestamp: new Date().toISOString()
        });
        
        throw new Error('OAuth server error. Please try again later or contact support.');
      }
      
      // eslint-disable-next-line no-console
      console.error('Token exchange error details:', {
        status: response.status,
        statusText: response.statusText,
        error: errorDetails,
        requestBody: {
          grant_type: 'authorization_code',
          client_id: CLIENT_ID,
          code: `${code.substring(0, 10)}...`, // Log only first 10 chars for security
          redirect_uri: redirectUri,
          code_verifier: codeVerifier ? 'present' : 'missing'
        }
      });
      
      throw new Error(`Token exchange failed: ${response.status} ${response.statusText} - ${JSON.stringify(errorDetails)}`);
    }
    
    const tokenData = await response.json();
    
    // eslint-disable-next-line no-console
    console.log('✅ Token exchange successful');
    
    // Clear code_verifier after successful exchange
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem('oauth_code_verifier');
    }
    
    return tokenData;
  } catch (error) {
    // Clear code_verifier on error to prevent reuse
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem('oauth_code_verifier');
    }
    
    // eslint-disable-next-line no-console
    console.error('Token exchange error:', error);
    throw error;
  }
};

// Get user information using access token
export const getUserInfo = async (accessToken) => {
  const { AUTH_URL, ENDPOINTS } = OAUTH_CONFIG;
  
  try {
    const response = await fetch(`${AUTH_URL}${ENDPOINTS.USERINFO}`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`User info request failed: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('User info error:', error);
    throw error;
  }
};

// Refresh access token using refresh token
export const refreshAccessToken = async (refreshToken) => {
  const { AUTH_URL, CLIENT_ID, ENDPOINTS } = OAUTH_CONFIG;
  
  try {
    const response = await fetch(`${AUTH_URL}${ENDPOINTS.REFRESH}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        grant_type: 'refresh_token',
        client_id: CLIENT_ID,
        refresh_token: refreshToken
      })
    });
    
    if (!response.ok) {
      throw new Error(`Token refresh failed: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Token refresh error:', error);
    throw error;
  }
};

// Validate state parameter for CSRF protection
export const validateState = (receivedState, expectedState) => receivedState === expectedState;

// Check if we're in OAuth callback mode
export const isOAuthCallback = () => {
  if (typeof window === 'undefined') return false;
  return window.location.pathname === '/auth/oauth/streetlib/callback';
};

// Extract OAuth parameters from URL
export const extractOAuthParams = () => {
  if (typeof window === 'undefined') return null;
  
  const urlParams = new URLSearchParams(window.location.search);
  return {
    code: urlParams.get('code'),
    state: urlParams.get('state'),
    error: urlParams.get('error'),
    error_description: urlParams.get('error_description')
  };
};

// Get redirect URL after successful OAuth login
export const getRedirectUrl = (defaultRedirect = '/') => {
  if (typeof window === 'undefined') return defaultRedirect;
  
  // Check if there's a saved redirect URL
  const savedRedirect = window.sessionStorage.getItem('oauth_redirect');
  if (savedRedirect) {
    // eslint-disable-next-line no-console
    console.log('🔗 Using saved redirect URL:', savedRedirect);
    return savedRedirect;
  }
  
  // Check if there's a referer URL (user came from another page)
  const referer = document.referrer;
  if (referer) {
    try {
      const refererUrl = new URL(referer);
      const refererPath = refererUrl.pathname;
      
      // Only redirect to referer if it's from the same domain and not a login page
      if (refererUrl.origin === window.location.origin && 
          !refererPath.includes('/login') && 
          !refererPath.includes('/auth/')) {
        // eslint-disable-next-line no-console
        console.log('🔗 Using referer URL:', refererPath);
        return refererPath;
      }
    } catch (error) {
      // Invalid referer URL, fall back to default
      // eslint-disable-next-line no-console
      console.warn('Invalid referer URL:', referer);
    }
  }
  
  // If no referer, check current pathname
  const currentPath = window.location.pathname;
  if (currentPath !== '/login' && currentPath !== '/') {
    // eslint-disable-next-line no-console
    console.log('🔗 Using current pathname:', currentPath);
    return currentPath;
  }
  
  // eslint-disable-next-line no-console
  console.log('🔗 Using default redirect URL:', defaultRedirect);
  return defaultRedirect;
};

// Store redirect URL for OAuth flow
export const storeRedirectUrl = (redirectUrl) => {
  if (typeof window === 'undefined') return '/';
  
  // If no redirect URL provided, try to determine from current context
  let finalRedirectUrl = redirectUrl;
  if (!finalRedirectUrl) {
    const currentPath = window.location.pathname;
    if (currentPath !== '/login' && currentPath !== '/') {
      finalRedirectUrl = currentPath;
    } else {
      finalRedirectUrl = '/';
    }
  }
  
  // eslint-disable-next-line no-console
  console.log('🔗 Storing redirect URL:', finalRedirectUrl);
  window.sessionStorage.setItem('oauth_redirect', finalRedirectUrl);
  return finalRedirectUrl;
};

// Starts the authorization-code flow: state, PKCE verifier, where to come back
// to, then the browser goes to the sign-in host. Shared by the /login button and
// by /login?sso=1, which starts it without one.
export const startOAuthLogin = async (redirectTo = null) => {
  const state = generateState();
  storeRedirectUrl(redirectTo);
  if (typeof window !== 'undefined') {
    window.sessionStorage.setItem('oauth_state', state);
  }
  window.location.href = await buildAuthUrl(state);
};
